import type { ReactNodeViewProps } from '@tiptap/react';
import type { MentionOptions as TTMentionOptions } from '@tiptap/extension-mention';
import type { MouseEvent } from 'react';

import { mergeAttributes } from '@tiptap/core';
import TTMention from '@tiptap/extension-mention';
import { PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Pill, PillWrapper } from '../components/PillNodeView';

export type MentionOptions = Pick<
  TTMentionOptions,
  'HTMLAttributes' | 'renderLabel' | 'suggestion'
>;

const PillNodeView = (props: ReactNodeViewProps) => {
  const { id, label: name, stamp } = props.node.attrs || {};

  const setCursor = () => {
    const pos = props.getPos();

    if (pos !== undefined) {
      props.editor
        .chain()
        .focus()
        .setTextSelection(pos + 1)
        .run();
    }
  };

  const handleRemove = (_id: string, ev: MouseEvent<HTMLElement>) => {
    ev.stopPropagation();
    props.deleteNode();

    const pos = props.getPos();

    if (pos !== undefined) {
      props.editor.chain().focus().setTextSelection(pos).run();
    }
  };

  return (
    <PillWrapper as="button">
      <Pill
        hideRemoveIcon={false}
        maxWidth="250px"
        id={id}
        name={name}
        stamp={stamp}
        onClick={setCursor}
        onRemove={handleRemove}
      />
    </PillWrapper>
  );
};

export const Mention = TTMention.extend<MentionOptions>({
  addOptions() {
    return {
      HTMLAttributes: {
        class: 'mention',
      },
      renderLabel({ options, node }) {
        return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`;
      },
      suggestions: [],
      suggestion: {
        char: '@',
        allowSpaces: true,
        pluginKey: new PluginKey('mention'),
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith('  ');

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();
        },
        allow: ({ editor, range }) => {
          const $from = editor.state.doc.resolve(range.from);
          const type = editor.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      stamp: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-stamp'),
        renderHTML: (attributes) => {
          if (!attributes.stamp) {
            return {};
          }
          return {
            'data-stamp': attributes.stamp,
          };
        },
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = node.attrs.label || '';
    return [
      'span',
      mergeAttributes(
        { 'data-mention': '' },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      label.trim(),
    ];
  },

  renderText({ node }) {
    const user = node.attrs.id;

    return `${this.options.suggestion.char}${user.label}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(PillNodeView);
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true;
              tr.insertText(
                this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              );
              return false;
            }
          });

          return isMention;
        }),
    };
  },
});
